import React from 'react'
import { X, Check } from 'lucide-react'
import ClientDashboardV3ScoreGauge from '../ClientDashboardV3ScoreGauge'

export interface HealthScoreDrawerProps {
  isOpen: boolean
  onClose: () => void
  score?: number
  scoreChange?: number | null
  avgVerticalScore?: number
  lastUpdated?: string
}

function getRatingInfo(score: number): { label: string; band: string; color: string; badgeBg: string; badgeText: string } {
  if (score >= 85) return { label: 'Excellent', band: '85–100', color: '#10B981', badgeBg: '#17B26A', badgeText: '#FFFFFF' }
  if (score >= 70) return { label: 'Average', band: '70–84', color: '#EAB308', badgeBg: '#FEFBE8', badgeText: '#A16207' }
  if (score >= 55) return { label: 'Good', band: '55–69', color: '#84CC16', badgeBg: '#ECFDF3', badgeText: '#027A48' }
  if (score >= 40) return { label: 'Fair', band: '40–54', color: '#F97316', badgeBg: '#FEF6EE', badgeText: '#B54708' }
  return { label: 'Poor', band: '0–39', color: '#EF4444', badgeBg: '#FEF3F2', badgeText: '#B42318' }
}

export function HealthScoreDrawer({
  isOpen,
  onClose,
  score = 73,
  scoreChange = 4,
  avgVerticalScore = 64,
  lastUpdated = 'May 06, 2026',
}: HealthScoreDrawerProps) {
  if (!isOpen) return null

  const ratingInfo = getRatingInfo(score)

  const scoreBands = [
    { label: 'Excellent', range: '85–100', color: '#10B981' },
    { label: 'Good', range: '55–69', color: '#84CC16' },
    { label: 'Average', range: '70–84', color: '#EAB308', isCurrent: score >= 70 && score <= 84 },
    { label: 'Fair', range: '40–54', color: '#F97316', isCurrent: score >= 40 && score <= 54 },
    { label: 'Poor', range: '0–39', color: '#EF4444', isCurrent: score < 40 },
  ]

  const calculationCards = [
    {
      title: 'Gross Margin',
      weight: 'HEAVIEST WEIGHT',
      weightBg: 'bg-purple-100 text-purple-700',
      badge: 'Excellent',
      badgeBg: 'bg-emerald-600 text-white',
      description: 'The percentage of revenue kept after cost of goods — the foundation of a healthy retail business.',
      stat: '54.2%',
      benchmark: 'Benchmark 48.1%',
      barColor: 'bg-emerald-500',
      widthPct: '100%',
    },
    {
      title: 'Sales Growth',
      weight: 'HIGH WEIGHT',
      weightBg: 'bg-blue-100 text-blue-700',
      badge: 'Good',
      badgeBg: 'bg-emerald-100 text-emerald-700',
      description: 'Year-over-year revenue change, benchmarked against peers in your vertical and store size range.',
      stat: '+12.3%',
      benchmark: 'Benchmark +8.1%',
      barColor: 'bg-blue-600',
      widthPct: '80%',
    },
    {
      title: 'Markdowns',
      weight: 'MODERATE WEIGHT',
      weightBg: 'bg-slate-100 text-slate-600',
      badge: 'Good',
      badgeBg: 'bg-emerald-100 text-emerald-700',
      description: 'How much inventory is being discounted to clear — lower is better. High markdowns signal over-buying or poor sell-through.',
      stat: '42.5%',
      benchmark: 'Benchmark 38.0%',
      barColor: 'bg-amber-400',
      widthPct: '65%',
    },
    {
      title: 'Annual Turn',
      weight: 'MODERATE WEIGHT',
      weightBg: 'bg-slate-100 text-slate-600',
      badge: 'Good',
      badgeBg: 'bg-emerald-100 text-emerald-700',
      description: 'How many times your inventory sells through in a year. Higher turns mean fresher product and better cash flow.',
      stat: '2.1×',
      benchmark: 'Benchmark 3.4×',
      barColor: 'bg-red-500',
      widthPct: '40%',
    },
    {
      title: 'Inventory Freshness',
      weight: 'MODERATE WEIGHT',
      weightBg: 'bg-slate-100 text-slate-600',
      badge: 'Good',
      badgeBg: 'bg-emerald-100 text-emerald-700',
      description: 'The percentage of your inventory that has arrived within the last 90 days — fresher inventory drives stronger sell-through.',
      stat: '31%',
      benchmark: 'Benchmark 45%',
      barColor: 'bg-amber-500',
      widthPct: '60%',
    },
  ]

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-xs transition-opacity animate-in fade-in duration-200">
      <div
        className="fixed inset-0"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative z-10 flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl animate-in slide-in-from-right duration-300">
        {/* Top Header */}
        <div className="flex items-start justify-between border-b border-slate-100 p-6 pb-4">
          <div className="flex flex-col gap-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">RETAILORBIT HEALTH SCORE™</span>
            <h2 className="text-xl font-bold text-slate-900">What does my score mean?</h2>
            <p className="text-xs text-slate-500">Powered by Management One — 40+ years of independent retail data</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content Body - 2 Columns */}
        <div className="grid flex-1 grid-cols-1 overflow-y-auto divide-y divide-slate-100 md:grid-cols-[280px,1fr] md:divide-x md:divide-y-0">
          {/* Left Column */}
          <div className="flex flex-col gap-6 bg-slate-50/50 p-6">
            {/* Gauge Box */}
            <div className="flex flex-col items-center rounded-2xl border border-slate-200/80 bg-white p-5 text-center shadow-2xs">
              <ClientDashboardV3ScoreGauge className="w-full max-w-[200px]" score={score} />
              
              <span className="mt-1 text-sm font-bold text-slate-800">{ratingInfo.label}</span>
              
              {scoreChange !== null && scoreChange !== undefined && (
                <span className="text-xs font-semibold text-emerald-600">
                  +{scoreChange} pts from last month
                </span>
              )}
              
              <span className="text-xs text-slate-500">
                Avg in your vertical: {avgVerticalScore}
              </span>

              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-3xl font-extrabold text-slate-900">{score}</span>
                <span className="text-sm font-medium text-slate-400">/100</span>
              </div>

              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-3.5 py-1 text-xs font-bold text-slate-900 shadow-2xs">
                <Check className="h-3.5 w-3.5 stroke-[3]" />
                <span>{ratingInfo.label}</span>
              </div>
            </div>

            {/* Score Bands */}
            <div className="flex flex-col gap-3">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">SCORE BANDS</span>
              <div className="flex flex-col gap-2.5">
                {scoreBands.map((band) => (
                  <div key={band.label} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: band.color }} />
                      <span className="font-semibold text-slate-700">{band.label}</span>
                      {band.isCurrent && (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">
                          You are here
                        </span>
                      )}
                    </div>
                    <span className="font-medium text-slate-400">{band.range}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="flex flex-col gap-6 overflow-y-auto p-6">
            <div className="flex flex-col gap-3 text-xs leading-relaxed text-slate-600">
              <p>
                Your <strong className="font-semibold text-indigo-700">RetailORBIT Health Score</strong> is a single number from 0-100 that measures the overall financial health of your store — calculated from M1&apos;s proprietary benchmarks across thousands of independent retailers in your vertical.
              </p>
              <p>
                M1 clients consistently outperform independent retail benchmarks. Your score shows where you stand within that high-performing network — and exactly what it takes to reach the top.
              </p>
            </div>

            {/* Highlight Callout Box */}
            <div className="rounded-xl border border-purple-200/80 bg-purple-50/70 p-4 text-xs leading-relaxed text-purple-950 shadow-2xs">
              Your score of <strong className="font-bold text-purple-950">{score}</strong> puts you <strong className="font-bold text-purple-950">above the average for your vertical ({avgVerticalScore})</strong> and in the <strong className="font-bold text-purple-950">top 35% of similar stores</strong>. A higher score means your business is operating efficiently — strong margins, healthy inventory turn, and controlled markdowns.
            </div>

            {/* Calculation Section */}
            <div className="flex flex-col gap-4">
              <span className="border-b border-slate-200 pb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                HOW YOUR SCORE IS CALCULATED
              </span>

              <div className="flex flex-col gap-3">
                {calculationCards.map((card) => (
                  <div key={card.title} className="flex flex-col gap-2 rounded-xl border border-slate-200/80 bg-white p-4 shadow-2xs">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-900">{card.title}</span>
                        <span className={`rounded-md px-2 py-0.5 text-[9px] font-bold ${card.weightBg}`}>
                          {card.weight}
                        </span>
                      </div>
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${card.badgeBg}`}>
                        {card.badge}
                      </span>
                    </div>

                    <p className="text-xs leading-relaxed text-slate-500">
                      {card.description}
                    </p>

                    <div className="mt-1 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5 font-bold text-slate-800">
                        <span>{card.stat}</span>
                        <span className="text-slate-400 font-normal">·</span>
                        <span className="font-normal text-slate-500">{card.benchmark}</span>
                      </div>
                    </div>

                    <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden mt-1">
                      <div className={`h-full ${card.barColor} rounded-full`} style={{ width: card.widthPct }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-1.5 border-t border-slate-200 bg-slate-50 px-6 py-3.5 text-xs text-slate-500">
          <span>ⓘ Scores update daily</span>
          <span>·</span>
          <span>Last updated: {lastUpdated}</span>
        </div>
      </div>
    </div>
  )
}
